import type { InvoiceDate, InvoiceFilter } from '@vy/types';
import { useCallback, useEffect, useState } from 'react';
import type { FilterLineType } from '../../../components/new-design/vyTableFilter';
import { useDebounce } from '../../../hooks/use-debounce';
import { useDialog } from '../../../hooks/use-dialog';
import { usePageParam } from '../../../hooks/search-params/use-page-param';
import { useSyncQueryParamToState } from '../../../hooks/search-params/use-sync-query-param-to-state';
import { MONTHS_LABELS } from '../../customers/OldCustomerOverviewPage/customer-view-page-components/BillingScheduleChart/billing-schedule-chart-consts';
import { useFiltersParam } from './use-filters-param';
import { useDatesParam } from './use-months-param';
import { useQueryInputParams } from './use-query-input-param';

export const useInvoicesFilters = () => {
  const [selectedDates, setSelectedDates] = useDatesParam();
  const [queryParam, setQueryParam] = useQueryInputParams();
  const [query, setQuery] = useState(queryParam ?? '');
  const debouncedQuery = useDebounce(query);
  const [filters, setFilters] = useFiltersParam();
  const [, setPage] = usePageParam();

  const {
    isDialogOpen: isFilterInvoicesOpen,
    handleOpen: openFilterInvoices,
    handleClose: closeFilterInvoices,
  } = useDialog();

  useEffect(() => {
    setQueryParam(debouncedQuery);
  }, [debouncedQuery]);

  useSyncQueryParamToState(queryParam, setQuery);

  const handleSearchInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    },
    [],
  );

  const handleCalendarChange = useCallback(
    (calendarMonths: InvoiceDate[]) => {
      const dates = calendarMonths.map((date: InvoiceDate) => ({
        month: MONTHS_LABELS.indexOf(date.month).toString(),
        year: date.year,
      }));

      setSelectedDates(dates);
      setPage(0);
    },
    [],
  );

  const handleFilterClick = useCallback(
    (filterLines: FilterLineType[]) => {
      setFilters(filterLines as InvoiceFilter[]);
      setPage(0);
    },
    [],
  );

  const getDefaultSelectedMonths = useCallback(() => {
    return (
      selectedDates?.map((date: InvoiceDate) => ({
        month: MONTHS_LABELS[Number(date.month)],
        year: date.year,
      })) ?? []
    );
  }, [selectedDates]);

  return {
    // Search
    query,
    handleSearchInputChange,

    // Date filtering
    selectedDates,
    handleCalendarChange,
    getDefaultSelectedMonths,

    // Filter handling
    filters,
    handleFilterClick,

    // Filter dialog
    isFilterInvoicesOpen,
    openFilterInvoices,
    closeFilterInvoices,
  };
};
